import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaFileInvoiceDollar } from 'react-icons/fa';
import useYearSelector from '../../hooks/useYearSelector';
import AnioStep from '../../components/DeclaracionRenta/Wizard/AnioStep';
import DJJGateStep from '../../components/DeclaracionRenta/Wizard/DJJGateStep';
import RegimenStep from '../../components/DeclaracionRenta/Wizard/RegimenStep';
import ModuleRenderer from '../../components/DeclaracionRenta/Wizard/ModuleRenderer';
import { getAvailableRegimens } from '../../components/DeclaracionRenta/F22/registry';
import { getRenta } from '../../services/declaracionRenta/f22Service';
import { useAuth } from '../../context/AuthContext';
import '../../styles/pages/ServiciosSII/DeclaracionRentaPage.css';

const STEPS = {
    ANIO: 'anio',
    CHECKING: 'checking',
    GATE: 'gate',
    REGIMEN: 'regimen',
    MODULE: 'module',
};

/**
 * Declaración de Renta: wizard (Año → régimen → módulo), con una comprobación
 * automática tras elegir el año: si ya existe una renta guardada para ese
 * año, se salta el gate de DJJ y el paso de régimen y se va directo al
 * módulo (que la muestra de inmediato) — si ya hay renta calculada, es
 * porque las DJJ ya se prepararon. Solo si no hay nada guardado se pregunta
 * por las DJJ y se pide elegir régimen. Ver
 * src/components/DeclaracionRenta/F22/registry.js para cómo se registra un
 * año/régimen nuevo.
 */
const DeclaracionRentaPage = () => {
    const navigate = useNavigate();
    const { selectedCompany, token } = useAuth();
    const idCompany = selectedCompany?.id_company || selectedCompany?.id;
    const { selectedYear, setSelectedYear, yearOptions } = useYearSelector(null, 1, 0);
    const [step, setStep] = useState(STEPS.ANIO);
    const [selectedRegimen, setSelectedRegimen] = useState('14d3');

    const handleFaltaPreparar = () => {
        navigate(`/declaraciones-juradas?anio=${selectedYear}`);
    };

    const handleAnioContinuar = async () => {
        const regimenParaBuscar = getAvailableRegimens(selectedYear)[0];

        if (!regimenParaBuscar || !idCompany || !token) {
            setStep(STEPS.GATE);
            return;
        }

        setStep(STEPS.CHECKING);
        try {
            const result = await getRenta(idCompany, selectedYear, regimenParaBuscar);
            if (result.success) {
                setSelectedRegimen(regimenParaBuscar);
                setStep(STEPS.MODULE);
            } else {
                setStep(STEPS.GATE);
            }
        } catch (err) {
            setStep(STEPS.GATE);
        }
    };

    return (
        <div className="declaracion-renta-page">
            <div className="page-header">
                <div className="header-content">
                    <div className="title-section">
                        <FaFileInvoiceDollar className="page-icon" />
                        <div>
                            <h1>Declaración de Renta {step !== STEPS.ANIO ? `AT ${selectedYear}` : ''}</h1>
                            <p className="page-subtitle">
                                Formulario 22 - Declaración anual de impuesto a la renta
                            </p>
                        </div>
                    </div>
                    <button className="dr-btn-volver" onClick={() => navigate('/dashboard')}>
                        <FaArrowLeft /> Volver al Dashboard
                    </button>
                </div>
            </div>

            <div className="declarar-renta-container">
                {step === STEPS.ANIO && (
                    <AnioStep
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        yearOptions={yearOptions}
                        onContinuar={handleAnioContinuar}
                    />
                )}

                {step === STEPS.CHECKING && (
                    <div className="f22-loading">
                        <div className="f22-spinner" />
                        <p>Buscando declaración guardada...</p>
                    </div>
                )}

                {step === STEPS.GATE && (
                    <DJJGateStep
                        anioTributario={selectedYear}
                        onConfirmar={() => setStep(STEPS.REGIMEN)}
                        onFaltaPreparar={handleFaltaPreparar}
                    />
                )}

                {step === STEPS.REGIMEN && (
                    <RegimenStep
                        anioTributario={selectedYear}
                        selectedRegimen={selectedRegimen}
                        onRegimenChange={setSelectedRegimen}
                        onContinuar={() => setStep(STEPS.MODULE)}
                    />
                )}

                {step === STEPS.MODULE && (
                    <ModuleRenderer anioTributario={selectedYear} regimen={selectedRegimen} />
                )}
            </div>

            <div className="page-footer">
                <p className="footer-info">
                    <strong>Nota:</strong> Las declaraciones de renta deben ser presentadas ante el SII
                    durante el mes de abril de cada año. Asegúrese de contar con toda la documentación
                    respaldatoria antes de enviar.
                </p>
            </div>
        </div>
    );
};

export default DeclaracionRentaPage;
